import { Link } from 'react-router-dom';
import { Notice } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /privacy — Privacy Policy stub. Full text ships when Legal has finalised
 * it. The operative summary below is honest today and matches what the
 * product actually does.
 *
 * Note: an authenticated `/privacy` route already exists in the app-shell
 * for signed-in users to see their own privacy controls. This route is
 * for the public marketing surface — see router.tsx for both wirings.
 */
export function PrivacyPublicPage() {
  return (
    <>
      <PageMeta
        title="Privacy Policy"
        description="Kollez Privacy Policy. What we collect, why we collect it, and what you can do about it — under India's DPDP Act, 2023."
        canonical="/privacy"
      />

      <section className="mx-auto max-w-page-narrow px-5 py-16 lg:px-10 lg:py-24 grid gap-6">
        <div>
          <p className="eyebrow">Legal</p>
          <h1 className="display text-ink-primary mt-2">Privacy Policy</h1>
          <p className="mt-4 helper">Effective date: to be published on execution</p>
        </div>

        <Notice tone="info">
          The full Privacy Policy is being finalised by our legal team. The summary
          below covers the operative parts and matches what the product actually does
          today. You can{' '}
          <Link className="link" to="/contact?topic=privacy">request the current draft</Link>{' '}
          or read our{' '}
          <Link className="link" to="/security">Security &amp; Privacy overview</Link>{' '}
          for the operational picture.
        </Notice>

        <div className="grid gap-6 prose-read max-w-none">
          <TermsSection heading="1. What we collect">
            <ul>
              <li><b>Students:</b> name, phone, email, college code, academic records (marks, program, branch), skills, resume, application history, interview outcomes, offers.</li>
              <li><b>Partner recruiters:</b> company name, contact person, work email, mobile, verified employer details, opportunities posted.</li>
              <li><b>Institution staff:</b> name, work email, mobile, role, institution and department affiliation.</li>
              <li><b>Everyone:</b> device / browser info for security and abuse prevention; audit logs of actions taken on the platform.</li>
            </ul>
          </TermsSection>

          <TermsSection heading="2. Why we collect it">
            <p>
              To run the placement register — match students with opportunities they
              are eligible for, help institutions run drives, help partners find
              talent, and prove outcomes for NIRF / NAAC / AICTE reporting where the
              institution has authorised us.
            </p>
          </TermsSection>

          <TermsSection heading="3. Consent">
            <p>
              You consent at signup. For students under 18, verifiable parental
              consent is required before we store your personal details, per India's
              DPDP Act, 2023.
            </p>
          </TermsSection>

          <TermsSection heading="4. Who sees what">
            <ul>
              <li>Your college's TPO and authorised staff see the parts they need to run drives — name, marks, applications, offers. They do <b>not</b> see your Nexie chats or your mock-interview transcripts.</li>
              <li>Partners see the eligibility signals for your applications; contact info is <b>hidden until they shortlist you</b>.</li>
              <li>Kollez engineers see only what is required to support your account.</li>
            </ul>
          </TermsSection>

          <TermsSection heading="5. Where your data lives">
            <p>
              In India. On servers we control. Our subprocessors are listed in the{' '}
              <Link className="link" to="/security">Security</Link> page.
            </p>
          </TermsSection>

          <TermsSection heading="6. What you can do">
            <ul>
              <li><b>Export:</b> download your data anytime from Settings, in JSON or CSV.</li>
              <li><b>Delete:</b> request deletion anytime. We soft-delete for 60 days (in case of accident) and then hard-delete.</li>
              <li><b>Correct:</b> edit your profile inline anywhere.</li>
              <li><b>Withdraw consent:</b> close your account at any time. No exit fees.</li>
            </ul>
          </TermsSection>

          <TermsSection heading="7. Data breach notification">
            <p>
              If we learn of a security breach affecting your data, we will notify you
              and the Data Protection Board of India within the DPDP-mandated
              timelines. We publish incident post-mortems on{' '}
              <Link className="link" to="/status">status</Link> within 48 hours.
            </p>
          </TermsSection>

          <TermsSection heading="8. Contact our Data Protection Officer">
            <p>
              Email <a className="link" href="mailto:dpo@kollez.ai">dpo@kollez.ai</a> for
              any DPDP-related question, request, or complaint. We respond within
              7 working days.
            </p>
          </TermsSection>
        </div>

        <p className="helper text-center mt-4">
          Questions?{' '}
          <Link className="link" to="/contact?topic=privacy">Get in touch with our team</Link>.
        </p>
      </section>
    </>
  );
}

function TermsSection({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2">
      <h2 className="font-serif text-xl">{heading}</h2>
      <div className="text-ink-secondary">{children}</div>
    </section>
  );
}
